import { useCallback, useEffect, useState } from "react";
import UploadMessages from "../../../components/UploadMessages";
import LocatorDetails, { LocatorsGrid } from "../../../components/Details";
import { useAppDispatch, useAppSelector } from "../../../store";
import FileUpload from "../../../components/FileUpload";
import Loader from "../../../components/Loader";
import { resetLocatorUpload } from "../../../store/reducers/LocatorsReducers";
import { getLocatorUploadStatus } from "../../../store/selectors/LocatorSelectors";
import { uploadLocatorAction } from "../../../store/actions/LocatorsActions";
import {
  getUploadStep,
  getIsUploadStarted,
} from "../../../store/selectors/DatasetSelectors";
import {
  resetDatasetSlice,
  setIsUploadStarted,
  updateDatasetStep,
} from "../../../store/reducers/DatasetReducer";

const LocatorDataset = () => {
  const dispatch = useAppDispatch();

  const locatorUploadStep = useAppSelector(getUploadStep);
  const locatorUploadStatus = useAppSelector(getLocatorUploadStatus);
  const isUploadStarted = useAppSelector(getIsUploadStarted);

  const [selectedFile, setSelectedFile] = useState<any>(null);

  const onCancelUpload = useCallback(() => {
    dispatch(setIsUploadStarted(false));
  }, [dispatch]);

  const handleFileSubmission = useCallback(() => {
    if (!selectedFile) return;

    const formData = new FormData();
    formData.append("image", selectedFile, selectedFile.name);
    dispatch(uploadLocatorAction(formData));
  }, [selectedFile, dispatch]);

  const fileUploadProps = useCallback(
    () => ({
      isFileUploading: locatorUploadStatus === "loading",
      selectedFile,
      onFileChange: setSelectedFile,
      onCancelUpload,
      handleFileSubmission,
      modelType: "locator",
      allowMultiple: false, // Locator dataset uploads use single files
    }),
    [locatorUploadStatus, selectedFile, handleFileSubmission, onCancelUpload]
  );

  useEffect(() => {
    if (locatorUploadStatus !== "success") return;
    dispatch(updateDatasetStep(2));
  }, [locatorUploadStatus, dispatch]);

  useEffect(() => {
    return function () {
      if (!isUploadStarted) return;
      dispatch(resetDatasetSlice());
      dispatch(resetLocatorUpload());
    };
  }, [dispatch, isUploadStarted]);

  const onClickAway = () => {
    dispatch(updateDatasetStep(1));
    setSelectedFile(null);
  };

  const loadingMessage = <Loader />;

  const successMessage = (
    <UploadMessages
      msgType="success"
      message="Successfully added to dataset!"
      onClickForward={onClickAway}
    />
  );

  const errorMessage = (
    <UploadMessages
      msgType="error"
      message="Oops, something went wrong..."
      onClickBack={onClickAway}
    />
  );

  const defaultView =
    locatorUploadStatus === "error"
      ? errorMessage
      : locatorUploadStatus === "success"
      ? successMessage
      : loadingMessage;

  const datasetContent = useCallback(() => {
    if (!isUploadStarted) return <LocatorsGrid />;

    switch (locatorUploadStep) {
      case 1:
        return <FileUpload {...fileUploadProps()} />;
      case 2:
        return <LocatorDetails />;
      default:
        return defaultView;
    }
  }, [isUploadStarted, locatorUploadStep, defaultView, fileUploadProps]);

  return datasetContent();
};

export default LocatorDataset;
